/**
 * WHERE THE API IS. The only file that reads import.meta.env.
 *
 * VITE_API_BASE_URL names one server from `api/servers.ts`. Unset, a live
 * build uses the dev server (`https://dev.pharmos.pcfhk.org/api`). A mock
 * build with it unset stays on the page's own origin, which is what lets the
 * MSW handlers (any origin, path `/v1/...`) answer. It is read on each call rather than once,
 * so a test can stub it.
 *
 * VITE_API_MODE=mock starts the MSW worker and answers every request from
 * src/api/msw. Only "mock" means mock; anything else talks to the server.
 */
import { DEFAULT_API_BASE_URL } from "./servers";

export function apiBaseUrl(): string {
  const configured = (import.meta.env.VITE_API_BASE_URL ?? "").trim().replace(/\/+$/, "");
  if (configured !== "") return configured;
  if (import.meta.env.VITE_API_MODE === "mock") return "";
  return DEFAULT_API_BASE_URL;
}

export function apiMode(): "mock" | "live" {
  return import.meta.env.VITE_API_MODE === "mock" ? "mock" : "live";
}

/**
 * THE BACKEND'S PUBLIC KEY FOR PASSWORDS — RSA, SubjectPublicKeyInfo, base64.
 *
 * One per API host: UAT and production hold different private keys, so the key
 * is baked into the build beside `VITE_API_BASE_URL`, never into the code.
 * Public, so a `VITE_` variable is the right home. Whitespace is dropped so a
 * key pasted across lines still reads. Null when the build was given none:
 * `api/password-cipher.ts` decides what that means.
 */
export function passwordPublicKey(): string | null {
  const key = (import.meta.env.VITE_PASSWORD_PUBLIC_KEY ?? "").replace(/\s+/g, "");
  return key === "" ? null : key;
}

/**
 * WHETHER THE DEVELOPER'S OWN PAGES ANSWER — `/_kit` and anything like it.
 *
 * A preview page carries sample values and explains the kit to whoever is
 * building it. At a pharmacy it is a page somebody eventually finds, reads as
 * the product, and reports; so in a build that talks to a real server it is not
 * a route at all.
 *
 * A MOCK BUILD KEEPS IT, which is what the browser gate needs: the fit audit
 * runs against a PRODUCTION build served with `VITE_API_MODE=mock`, and that
 * page is what it measures. Nothing answering a real API answers here.
 */
export function previewRoutesOn(): boolean {
  return import.meta.env.DEV || apiMode() === "mock";
}

/** Joins the base URL and a path. A missing leading slash is added, never guessed around. */
export function apiUrl(path: string): string {
  return `${apiBaseUrl()}${path.startsWith("/") ? path : `/${path}`}`;
}

export const API_PREFIX = "/api/v1";
